customElements.whenDefined('slide-show').then(() => {
  if (!customElements.get('testimonial-tab-slideshow')) {
    class TestimonialTabSlideshow extends customElements.get('slide-show') {
      init() {
        if (!this.isConnected || this.slides) return;
        super.init();
        this.resizeObserver = new ResizeObserver(() => this.alignSlide());
        this.resizeObserver.observe(this);
      }

      connectedCallback() {
        if (!this.slides) return;
        this.resizeObserver?.observe(this);
        if (this.autoplayEnabled) this.addObserver();
        this.alignSlide();
      }

      disconnectedCallback() {
        if (this.slideshow) this.stopAutoplay();
        this.viewportObserver?.disconnect();
        this.resizeObserver?.disconnect();
        clearTimeout(this.transitionLock);
        this.transitioning = false;
      }

      get panelVisible() {
        return this.isConnected && !this.closest('[role="tabpanel"]')?.hidden;
      }

      alignSlide() {
        if (!this.panelVisible || !this.slides) return;
        this.slideshow.scrollTo({ left: this.slides[this.currentIndex].offsetLeft, behavior: 'instant' });
      }

      setSlideVisibility() {
        super.setSlideVisibility();
        this.alignSlide();
      }

      setActiveSlide(index, direction) {
        if (!this.slides || this.transitioning || index === this.currentIndex) return;
        if (index < 0 || index >= this.slides.length) return;
        this.transitioning = true;
        super.setActiveSlide(index, direction);
        this.transitionLock = setTimeout(() => { this.transitioning = false; }, 220);
      }

      addObserver() {
        this.viewportObserver?.disconnect();
        this.viewportObserver = new IntersectionObserver((entries) => {
          this.inViewport = entries[0].isIntersecting;
          if (this.inViewport && this.panelVisible) this.resumeAutoplay();
          else this.pauseAutoplay();
        });
        this.viewportObserver.observe(this);
      }

      startAutoplay() {
        clearInterval(this.autoplayTimer);
        clearTimeout(this.resumeTimer);
        if (!this.panelVisible || this.inViewport === false || this.slides.length < 2) {
          this.autoplayTimeLeft = this.autoplaySpeed;
          this.setSlideshowState('paused');
          return;
        }
        super.startAutoplay();
      }

      resumeAutoplay() {
        if (!this.panelVisible || this.inViewport === false) return;
        super.resumeAutoplay();
      }
    }
    customElements.define('testimonial-tab-slideshow', TestimonialTabSlideshow);
  }

  if (!customElements.get('testimonials-tabs')) {
    class TestimonialsTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.addEventListener('click', (event) => {
          const tab = event.target.closest('[role="tab"]');
          if (this.tabs.includes(tab)) this.selectTab(tab);
        }, { signal });
        this.addEventListener('keydown', (event) => {
          const current = this.tabs.indexOf(event.target);
          if (current < 0) return;
          let next;
          const direction = document.dir === 'rtl' ? -1 : 1;
          if (event.key === 'ArrowRight') next = current + direction;
          if (event.key === 'ArrowLeft') next = current - direction;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          const tab = this.tabs[(next + this.tabs.length) % this.tabs.length];
          this.selectTab(tab);
          tab.focus({ preventScroll: true });
        }, { signal });
        document.addEventListener('shopify:block:select', (event) => {
          if (!this.contains(event.target)) return;
          const panel = event.target.closest('[role="tabpanel"]');
          const tab = this.tabs.find((item) => item.getAttribute('aria-controls') === panel?.id);
          if (!tab) return;
          this.selectTab(tab);
          const slideshow = panel.querySelector('testimonial-tab-slideshow');
          slideshow.init();
          const slide = event.target.closest('.slideshow__slide');
          if (slide) slideshow.setActiveSlide(Number(slide.dataset.index));
          if (slideshow.autoplayEnabled) slideshow.pauseAutoplay();
        }, { signal });
        this.addEventListener('focusin', () => this.finishReveal(), { signal });
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.initReveal(), { once: true, signal });
        } else {
          this.initReveal();
        }
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.revealMedia?.revert();
        this.revealMedia = null;
        this.revealTween = null;
      }

      initReveal() {
        if (!this.isConnected || this.hasRevealed || this.revealMedia) return;
        if (window.Shopify?.designMode || !window.gsap || !window.ScrollTrigger) return;
        if (this.getBoundingClientRect().bottom <= 0 || this.contains(document.activeElement)) return;
        const { gsap, ScrollTrigger } = window;
        gsap.registerPlugin(ScrollTrigger);
        this.revealMedia = gsap.matchMedia();
        this.revealMedia.add('(prefers-reduced-motion: no-preference)', () => {
          if (this.hasRevealed) return;
          const targets = [
            this.querySelector('.testimonials-tabs__tabs'),
            this.panels.find((panel) => !panel.hidden)
          ].filter(Boolean);
          if (!targets.length) return;
          this.revealTween = gsap.fromTo(targets,
            { opacity: 0, y: 24 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.12,
              ease: 'power2.out',
              clearProps: 'opacity,transform',
              onStart: () => { this.hasRevealed = true; },
              scrollTrigger: { trigger: this, start: 'top 85%', once: true }
            }
          );
        }, this);
        const refresh = () => {
          if (this.isConnected) this.revealTween?.scrollTrigger?.refresh();
        };
        window.addEventListener('load', refresh, { once: true, signal: this.controller.signal });
        document.fonts?.ready.then(refresh);
      }

      finishReveal() {
        this.hasRevealed = true;
        this.revealMedia?.revert();
        this.revealMedia = null;
        this.revealTween = null;
      }

      selectTab(selectedTab) {
        this.finishReveal();
        const panelId = selectedTab.getAttribute('aria-controls');
        this.tabs.forEach((tab) => {
          const selected = tab === selectedTab;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        selectedTab.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
        this.panels.forEach((panel) => {
          const selected = panel.id === panelId;
          const slideshow = panel.querySelector('testimonial-tab-slideshow');
          if (!selected && slideshow.slides && slideshow.autoplayEnabled) slideshow.pauseAutoplay();
          panel.hidden = !selected;
          if (selected) {
            slideshow.init();
            slideshow.alignSlide();
            if (slideshow.autoplayEnabled) slideshow.resumeAutoplay();
          }
        });
      }
    }
    customElements.define('testimonials-tabs', TestimonialsTabs);
  }
});
